import { User } from '@project44/ui-utils';
import { SharedModels } from '@project44-models/iam-gateway';

import { TenantSpecificTenantAdminRolesEnum } from '../enums';
import {
  IRolesEditorPolicy,
  TUnifiedRole,
  TUnifiedRoleCollectionDto,
  TUnifiedRolesMap,
} from '../interfaces';
import { UnifiedRolesEditor } from '../UnifiedRolesEditor';

export abstract class UnifiedRolesEditorPolicy implements IRolesEditorPolicy {
  public abstract canUserPrincipalUpdateTenantAgnosticUnifiedRoles(
    userPrincipal: User,
  ): boolean;

  public abstract canUserPrincipalUpdateTenantSpecificUnifiedRoles(
    userPrincipal: User,
  ): boolean;

  public abstract removeUnifiedRole(
    unifiedRoleMap: TUnifiedRolesMap,
    unifiedRoleIdToRemove: string,
  ): TUnifiedRolesMap;

  public abstract hasUnifiedRoleAssigned(
    unifiedRoleToCheck: TUnifiedRole,
  ): boolean;

  public abstract mapUserRoles(
    userRoles: TUnifiedRoleCollectionDto,
  ): TUnifiedRolesMap;

  public abstract removeAllUnifiedRoles(
    unifiedRoleMap: TUnifiedRolesMap,
  ): TUnifiedRolesMap;

  public abstract addUnifiedRole(
    unifiedRoleMap: TUnifiedRolesMap,
    unifiedRoleIdToAdd: string,
  ): TUnifiedRolesMap;

  public abstract addAllUnifiedRoles(
    unifiedRoleMap: TUnifiedRolesMap,
  ): TUnifiedRolesMap;

  public abstract composeResourceTenantAgnosticRolesMap(): TUnifiedRolesMap;

  public abstract composeResourceTenantSpecificRolesMap(): TUnifiedRolesMap;
  public abstract handleNMCRolesSupport(payload: {
    tenantRoles: SharedModels.UnifiedRoleCollectionViewModel;
    featureFlags: Record<string, boolean>;
  }): SharedModels.UnifiedRoleCollectionViewModel;

  public abstract composeUserTenantSpecificTenantAdminRoles(
    tenantSpecificTenantAdminRolesType: TenantSpecificTenantAdminRolesEnum,
    payload?: { featureFlags?: Record<string, boolean> },
  ): TUnifiedRolesMap;

  public abstract batchUnifiedRoles(payload: {
    roles: TUnifiedRolesMap;
    rolesToAdd: Array<TUnifiedRole>;
    rolesToRemove: Array<TUnifiedRole>;
  }): TUnifiedRolesMap;

  public abstract toUnifiedRoleCollectionDto(
    unifiedRoleMap: TUnifiedRolesMap,
  ): TUnifiedRoleCollectionDto;

  public abstract getAllAvailableUnifiedRoles(): TUnifiedRoleCollectionDto;

  public abstract isRoleTenantAdminRelatedRole(
    maybeTenantAdminRelatedRole?: TUnifiedRole,
  ): boolean;

  public abstract getPrevAssignedResourceUnifiedRoles(): TUnifiedRoleCollectionDto;

  public abstract addAllNonTenantAdminRelatedRolesToAssignedRoles(
    targetUnifiedRolesMap: TUnifiedRolesMap,
  ): TUnifiedRolesMap;

  public abstract removeAllNonTenantAdminRelatedRolesFromAssignedRoles(
    targetUnifiedRolesMap: TUnifiedRolesMap,
  ): TUnifiedRolesMap;

  public abstract compareUnifiedRolesState(payload: {
    resourceUnifiedRolesState: [TUnifiedRolesMap, TUnifiedRoleCollectionDto];
  }): boolean;
  public abstract readonly checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole: UnifiedRolesEditor['checkUnifiedRoleIsNMCLegacyTenantAdminUnifiedRole'];

  public abstract readonly checkUnifiedRoleIsTenantAdminUnifiedRole: UnifiedRolesEditor['checkUnifiedRoleIsTenantAdminUnifiedRole'];

  public abstract readonly checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole: UnifiedRolesEditor['checkUnifiedRoleIsNMCLegacyReadOnlyUnifiedRole'];
}
